import type { CreateItemInput, LibraryGroup, LibraryItem } from './schemas';

export interface InsertItemParams extends CreateItemInput {
  libraryItemId: string;
}

/**
 * Handle inserting a new library item with a client-generated ID.
 * The ID stays stable across client and server.
 */
export async function handleInsertItem(params: InsertItemParams): Promise<LibraryItem> {
  const response = await fetch('/api/library/items', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error('Failed to insert item');
  }

  return response.json();
}

/**
 * Handle updating a library item's group assignment.
 */
export async function handleUpdateItemGroup(
  itemId: string,
  groupId: string | null
): Promise<boolean> {
  const response = await fetch(`/api/library/items/${itemId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ libraryGroupId: groupId }),
  });

  if (!response.ok) {
    throw new Error('Failed to update item group');
  }

  return true;
}

/**
 * Handle deleting a library item.
 */
export async function handleDeleteItem(itemId: string): Promise<boolean> {
  const response = await fetch(`/api/library/items/${itemId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete item');
  }

  return true;
}

export interface CreateGroupParams {
  libraryGroupId: string;
  name: string;
  description?: string;
}

/**
 * Handle updating a library item's detail (groups, note, etc.).
 */
export async function handleUpdateItemDetail(
  itemId: string,
  detail: Record<string, unknown>
): Promise<boolean> {
  const response = await fetch(`/api/library/items/${itemId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ detail }),
  });

  if (!response.ok) {
    throw new Error('Failed to update item detail');
  }

  return true;
}

/**
 * Handle creating a new library group with a client-generated ID.
 */
export async function handleCreateGroup(params: CreateGroupParams): Promise<LibraryGroup> {
  const response = await fetch('/api/library/groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error('Failed to create group');
  }

  return response.json();
}

export async function handleUpdateGroup(
  groupId: string,
  updates: { name?: string; description?: string | null }
): Promise<boolean> {
  const response = await fetch(`/api/library/groups/${groupId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error('Failed to update group');
  }

  return true;
}

export async function handleDeleteGroup(groupId: string): Promise<boolean> {
  const response = await fetch(`/api/library/groups/${groupId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete group');
  }

  return true;
}
